import "./article-share-tools.scss";

import { useState, useEffect } from "react";
import { Link } from "design-system/components/primitives/link/link";
import { Icon } from "design-system/components/icons/icon";
import { SocialMediaIconProps } from "design-system/components/primitives/social-media-links/social-media-icon";

export interface ArticleShareToolsProps {
  url?: string;
  text: string;
}

/**
 * Exposes an easy way to share articles via social media or email
 *
 * ## See it in use on...
 * - An [article page](/story/example-pages-article-pages-big-art-topper--story)
 *
 * - **`id: P-003-000-00`**
 */
export function ArticleShareTools({ url, text }: ArticleShareToolsProps) {
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    setShareUrl(url || window.location.href);
  }, [url]);

  return (
    <div className="hbs-article-share-tools">
      <ul>
        <li>
          <Link
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURI(
              shareUrl,
            )}`}
            target="_blank"
            rel="noreferrer"
          >
            <Icon name="facebook" />
            <span className="hbs-global-visually-hidden">
              Share via Facebook
            </span>
          </Link>
        </li>
        <li>
          <Link
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURI(
              shareUrl,
            )}`}
            target="_blank"
            rel="noreferrer"
          >
            <Icon name="linkedin" />
            <span className="hbs-global-visually-hidden">
              Share via LinkedIn
            </span>
          </Link>
        </li>
      </ul>
      <hr aria-hidden="true" />
      <ul>
        <li>
          <button onClick={() => window.print()}>
            <Icon name="print" />
            <span className="hbs-global-visually-hidden">Print</span>
          </button>
        </li>
        <li>
          <Link
            href={`mailto:?subject=${encodeURI(
              "You have received a shared article",
            )}&body=${encodeURI(`${text}\n${shareUrl}`)}`}
          >
            <Icon name="mail" />
            <span className="hbs-global-visually-hidden">Share via email</span>
          </Link>
        </li>
      </ul>
    </div>
  );
}

export function GetArticleShareToolsFooter({
  text,
  url,
}: ArticleShareToolsProps): Array<SocialMediaIconProps> {
  const [shareUrl, setShareUrl] = useState("");
  const props: Array<SocialMediaIconProps> = [
    {
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURI(
        shareUrl,
      )}`,
      icon: "facebook",
      title: "Facebook",
    },
    {
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURI(
        shareUrl,
      )}`,
      icon: "linkedin",
      title: "LinkedIn",
    },
    {
      icon: "print",
      title: "Print",
      print: true,
    },
    {
      href: `mailto:?subject=${encodeURI(
        "You have received a shared article",
      )}&body=${encodeURI(`${text}\n${shareUrl}`)}`,
      icon: "mail",
      title: "Email",
    },
  ];

  useEffect(() => {
    setShareUrl(url || window.location.href);
  }, [url]);

  return props;
}
